// import { userStatus } from "@shared_packages/toolkit/types";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { AdvancedSearch } from "../../../../components/advanced-search/AdvancedSearch";
import { SearchFieldType } from "../../../../components/advanced-search/AdvancedSearch.interface";
import { AnimateAppearanceWrapper } from "../../../../components/animate-appearence/AnimateAppearanceWrapper";
import {
  Button,
  ButtonVariant,
  ModalHeader,
  PageTitle,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import { Modal } from "../../../../components/modal/Modal";
import { useTableContext } from "../../../../components/ui-kit/ReactTable/context/TableContext";
import {
  NotificationTypes,
  useNotification,
} from "../../../../hooks/useNotification";
import api from "../../../../util/api";
import { getApiErrorMessage } from "../../../../util/apiErrorMessage";
import { UserApiType } from "../../../../util/types";
import UsersTable from "../components/UsersTable";
import { usersSearchConfig } from "../config/searchConfig";
import { useUsersContext } from "../context";
import { useModalManager } from "../../../../context/ModalManager";
import { useAuthUserContext } from "../../../../context/AdministratorContextProvider";

const Container = styled.div<{ details?: boolean }>`
  display: flex;
  flex-direction: column;
  ${({ details }) => details && "margin-top: 30px;"}
`;

const TopLine = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  @media (max-width: 900px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  @media (max-width: 767px) {
    flex-direction: row;
    align-items: center;
  }
`;

const TableContainer = styled.div<{ details?: boolean }>`
  display: flex;
  flex-direction: column;
  background: #ffffff;
  ${({ details }) =>
    !details &&
    `border: 1px solid #eeeeee;
    border-radius: 10px;
    padding: 10px 30px 20px;`}

  box-sizing: border-box;
  @media (max-width: 900px) {
    padding: ${({ details }) => (details ? "0" : "12px")};
  }
`;

interface AttachUserModalProps {
  companyId?: string;
  onClose: () => void;
  onSuccess: () => void;
}

const AttachUserModal: React.FC<AttachUserModalProps> = ({
  companyId,
  onClose,
  onSuccess,
}) => {
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [users, setUsers] = useState<UserApiType[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserApiType | null>(null);
  // const [selectedRole, setSelectedRole] = useState<string>("");
  // const [roles, setRoles] = useState<RoleApiType[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalDocs, setTotalDocs] = useState(0);
  const { showNotification } = useNotification();

  // Fetch users based on filters
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const result = await api.getUsersList(1, 50, filters);
        setUsers(result.docs || []);
        setTotalDocs(result.totalDocs || 0);
      } catch (error) {
        console.error("Error fetching users:", error);
        setUsers([]);
        setTotalDocs(0);
      }
    };

    fetchUsers();
  }, [filters]);

  // useEffect(() => {
  //   const fetchRoles = async () => {
  //     try {
  //       const result = await api.getRolesList();
  //       setRoles(result);
  //       // Set default role if available
  //       if (result.length > 0) {
  //         setSelectedRole(result[0]._id);
  //       }
  //     } catch (error) {
  //       console.error("Error fetching roles:", error);
  //     }
  //   };

  //   fetchRoles();
  // }, []);

  const handleAttachUser = async () => {
    if (!selectedUser || !companyId) return;

    setLoading(true);
    try {
      await api.attachUserToCompany(
        selectedUser._id,
        companyId
        // selectedRole
      );
      showNotification("User attached successfully", NotificationTypes.SUCCESS);
      onClose();
      onSuccess();
    } catch (error: unknown) {
      showNotification(
        getApiErrorMessage(error, "Failed to attach user"),
        NotificationTypes.WARNING
      );
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "600px" }}>
      <Typography
        variant={TypographyVariant.BODY1}
        style={{ marginBottom: 20 }}
      >
        Search and select a user to attach to this company:
      </Typography>

      <div style={{ marginBottom: 20 }}>
        {/* Top search only: the backend filters users by `searchKeyword` (name / email). */}
        <AdvancedSearch
          config={[]}
          onChange={setFilters}
          searchPlaceholder="Search by name or email..."
        />
      </div>

      <div
        style={{ maxHeight: "400px", overflowY: "auto", marginBottom: "20px" }}
      >
        <UsersTable
          data={users}
          pagination={{
            page: 1,
            limit: 50,
            totalDocs,
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            onPaginationChange: () => undefined, // No pagination in modal
          }}
          onRowClick={(user) => setSelectedUser(user)}
          selectedUserId={selectedUser?._id}
        />
      </div>

      {selectedUser && (
        <div style={{ marginBottom: "20px" }}>
          <Typography
            variant={TypographyVariant.BODY1}
            style={{ marginBottom: 10 }}
          >
            Selected user: {selectedUser.firstName} {selectedUser.lastName} (
            {selectedUser.email})
          </Typography>
          {/* <Typography
            variant={TypographyVariant.BODY1}
            style={{ marginBottom: 10 }}
          >
            Select Role:
          </Typography>
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontSize: "14px",
            }}
          >
            {roles.map((role) => (
              <option key={role._id} value={role._id}>
                {role.name}
              </option>
            ))}
          </select> */}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <Button variant={ButtonVariant.OUTLINED} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant={ButtonVariant.CONTAINED}
          onClick={handleAttachUser}
          disabled={!selectedUser}
        >
          Attach User
        </Button>
      </div>
    </div>
  );
};

export interface UsersListProps {
  ownerId?: string;
  details?: boolean;
}
export const UsersList: React.FunctionComponent<UsersListProps> = ({
  ownerId,
  details,
}) => {
  const { authUser } = useAuthUserContext();
  const canManageUsers = authUser.policies?.actionsOnUsers === true;
  const [attachModalOpen, setAttachModalOpen] = React.useState(false);
  const { sortConfig } = useTableContext();
  const { showNotification } = useNotification();
  const { addModal, removeLastModal } = useModalManager();

  const {
    usersData,
    setusersFilters,
    onPaginationChange,
    onSortChange,
    totalDocs,
    limit,
    page,
    filters,
    setOwner,
  } = useUsersContext();

  const handleDetachUser = async (user: UserApiType) => {
    try {
      await api.detachUserFromCompany(user._id, ownerId!);
      showNotification("User detached successfully", NotificationTypes.SUCCESS);
      // Refresh the list
      window.location.reload();
    } catch (error: unknown) {
      showNotification(
        getApiErrorMessage(error, "Failed to detach user"),
        NotificationTypes.WARNING
      );
    }
  };
  useEffect(() => {
    setOwner(ownerId);
  }, [ownerId]);

  useEffect(() => {
    // Only update sort if sortConfig is defined and different from current sort
    if (sortConfig && sortConfig.field) {
      onSortChange(sortConfig);
    }
  }, [sortConfig, onSortChange]);

  const searchConfig = ownerId
    ? usersSearchConfig.filter(
        (field) => field.type !== SearchFieldType.SEARCH_BY_COMPANY_DROPDOWN
      )
    : usersSearchConfig;

  return (
    <AnimateAppearanceWrapper>
      <Container details={details}>
        <TopLine>
          <PageTitle title="Users" />
          {details && canManageUsers ? (
            <Button
              variant={ButtonVariant.CONTAINED}
              onClick={() => setAttachModalOpen(true)}
            >
              Attach User
            </Button>
          ) : null}
        </TopLine>
        <TableContainer details={details}>
          <AdvancedSearch
            config={searchConfig}
            onChange={setusersFilters}
            restoredValues={filters}
            searchPlaceholder="Search by name, email or phone"
          />
          <UsersTable
            data={usersData}
            pagination={{ page, limit, totalDocs, onPaginationChange }}
            onDetachUser={
              details && canManageUsers ? handleDetachUser : undefined
            }
          />
        </TableContainer>
      </Container>

      {attachModalOpen && (
        <Modal onHide={() => setAttachModalOpen(false)}>
          <div>
            <ModalHeader
              title="Attach User to Company"
              handleClose={() => setAttachModalOpen(false)}
            />
            <div style={{ padding: 20 }}>
              <AttachUserModal
                companyId={ownerId}
                onClose={() => setAttachModalOpen(false)}
                onSuccess={() => {
                  setAttachModalOpen(false);
                  // Refresh users list
                  window.location.reload();
                }}
              />
            </div>
          </div>
        </Modal>
      )}
    </AnimateAppearanceWrapper>
  );
};
